import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { environment_prod } from '../../../environments/environment.prod';
import { environment_test } from '../../../environments/environment.test';
import { environment_dev } from '../../../environments/environment.dev';
import { environment_us_prod } from '../../../environments/us/environment.us.prod';
import { environment_us_test } from '../../../environments/us/environment.us.test';
import { environment_us_dev } from '../../../environments/us/environment.us.dev';
import { getPxdDevEnvironmentFromSubdomain, setPxdSubdomainFromUrl } from './environment.pxd-dev';
import { getTestEnvOverride } from './environment.test-override';
import { logging_config } from '../../logging-config/logging-config';
import { logging_config_dev } from '../../logging-config/logging-config_dev';
import { Logger, LoggingService } from 'ionic-logging-service';
import { StorageService } from '../storage/storage.service';
import { Platform } from '@ionic/angular';
import { PlatformService } from '../platform/platform.service';

// ############### DEV-ONLY ENVIRONMENT OVERRIDE — SHIPS DISABLED ###############
// Resolved once at module load. When enabled (see environment.test-override.ts),
// every prod fallback in this service routes to the chosen dev/test environment so
// the IDP sign-in buttons — which have no domain input — hit that environment instead
// of prod. Ships disabled; enable / set the target in environment.test-override.ts.
// ⚠️ Reminder: an enabled override points the whole app at a dev env (fine for internal-test
// builds) — just don't let it ride into a PR to main unless intended. See that file.
const TEST_ENV_OVERRIDE = getTestEnvOverride();
// #############################################################################

@Injectable({
  providedIn: 'root'
})

export class EnvironmentService {

    public isCordova: boolean = this.platformService.isCordova();
    public environmentPrefix: string;
    public currentEnvironmentEndpoints: any;
    public loggingConfig = logging_config;
    private ENVIRONMENT_PREFIX_STORAGE_KEY = 'environmentPrefix';
    private logger: Logger;

    constructor(
        private loggingService: LoggingService,
        private platform: Platform,
        private platformService: PlatformService,
        private storageService: StorageService
    )
    {
        this.logger = loggingService.getLogger("[EnvironmentService]");
        const methodName = "ctor";
        this.logger.entry(methodName);
        console.log('## INIT ## EnvironmentService ##');
        if (this.isCordova) {
            this.storageService.getDatabaseState().subscribe((isReady: boolean) => {
                console.log('## isReady FROM ENV_SERVICE ##', isReady);
                if (isReady) {
                    this.setEnvironmentPrefixFromStorage().subscribe((environmentPrefix) => {});
                }
                });
        } else {
            const currentUrl = new URL(window.location.href);
            const originUrl = currentUrl.origin;
            console.log('originUrl', JSON.stringify(originUrl));
            if (!originUrl.includes('localhost')) {
                this.setEnvironmentVariablesFromUrl(originUrl).subscribe((environmentPrefix) => {});
            } else {
                this.setEnvironmentPrefixFromStorage().subscribe((environmentPrefix) => {});
            }
        }
    }

    public setEnvironmentPrefixFromStorage(): Observable<string> {
        this.logger.info('setEnvironmentPrefixFromStorage()');
        return new Observable((observer) => {
            this.storageService.getItemFromLocalStorage(this.ENVIRONMENT_PREFIX_STORAGE_KEY, 'setEnvironmentPrefixFromStorage()').subscribe((environmentPrefix: string | undefined) => {
                console.log('environmentPrefix FROM_STORAGE', environmentPrefix);
                if (environmentPrefix) {
                    switch (environmentPrefix) {
                        case 'environment_us_dev':
                            this.currentEnvironmentEndpoints = environment_us_dev
                            this.setLogginConfig(logging_config_dev);
                        break;
                        case 'environment_us_test':
                            this.currentEnvironmentEndpoints = environment_us_test
                            this.setLogginConfig(logging_config);
                        break;
                        case 'environment_us_prod':
                            environmentPrefix = this.resolveEnvironment(environment_us_prod, 'environment_us_prod', logging_config);
                        break;
                        case 'environment_dev':
                            this.currentEnvironmentEndpoints = environment_dev
                            this.setLogginConfig(logging_config_dev)
                        break;
                        case 'environment_test':
                            this.currentEnvironmentEndpoints = environment_test
                            this.setLogginConfig(logging_config_dev);
                        break;
                        case 'environment_prod':
                            environmentPrefix = this.resolveEnvironment(environment_prod, 'environment_prod', logging_config);
                            break;
                        default:
                            // Determine if this is a printix.dev or sandbox.printix.dev environment host
                            // Don't use regex in order to include broadest platform support.
                            const pos = environmentPrefix.indexOf('environment_pxd_');
                            if (pos == 0) {
                                const printixDevSubdomain = environmentPrefix.substring('environment_pxd_'.length) + '.printix.dev';
                                this.currentEnvironmentEndpoints = getPxdDevEnvironmentFromSubdomain(printixDevSubdomain);
                                this.setLogginConfig(logging_config_dev);
                            } else {
                                environmentPrefix = this.resolveEnvironment(environment_prod, 'environment_prod', logging_config);
                            }
                            break;
                    }
                } else {
                    environmentPrefix = this.resolveEnvironment(environment_prod, 'environment_prod', logging_config);
                }
                this.logger.info('setEnvironmentPrefixFromStorage() - environmentPrefix: ' + environmentPrefix);

                // FOR DEBUG //
                /////////////////////////////////////////////////////////
                // /* */ this.currentEnvironmentEndpoints = environment_dev
                // /* */ environmentPrefix = 'environment_dev'
                // /* */ this.setLogginConfig(logging_config_dev);
                /////////////////////////////////////////////////////////

                observer.next(environmentPrefix);
                observer.complete();
            });
        });
    }

    public setEnvironmentVariablesFromUrl(url): Observable<string> {
        this.logger.info('setEnvironmentVariablesFromUrl() - URL: ' + JSON.stringify(url));
        let environmentPrefix = 'environment_prod'
        return new Observable((observer) => {
            if (url.includes('.printix.dev')) {
                // Determine if this is a printix.dev or sandbox.printix.dev environment host
                // Don't use regex in order to include broadest platform support.
                const pxdSubdomain = setPxdSubdomainFromUrl(url);

                if (pxdSubdomain != '') {
                    this.currentEnvironmentEndpoints = getPxdDevEnvironmentFromSubdomain(pxdSubdomain);
                    this.setLogginConfig(logging_config_dev);
                    environmentPrefix = this.currentEnvironmentEndpoints.environmentPrefix;
                }
            } else if (url.includes('.us.')) {
                if (url.includes('devenv.us.printix.net')) {
                    this.currentEnvironmentEndpoints = environment_us_dev;
                    this.setLogginConfig(logging_config_dev);
                    environmentPrefix = 'environment_us_dev';
                } else if (url.includes('testenv.us.printix.net')) {
                    this.currentEnvironmentEndpoints = environment_us_test;
                    this.setLogginConfig(logging_config);
                    environmentPrefix = 'environment_us_test';
                } else {
                    environmentPrefix = this.resolveEnvironment(environment_us_prod, 'environment_us_prod', logging_config);
                }
            } else {
                if (url.includes('devenv.printix.net')) {
                    this.currentEnvironmentEndpoints = environment_dev;
                    this.setLogginConfig(logging_config_dev)
                    environmentPrefix = 'environment_dev';
                } else if (url.includes('testenv.printix.net')) {
                    this.currentEnvironmentEndpoints = environment_test;
                    this.setLogginConfig(logging_config_dev);
                    environmentPrefix = 'environment_test';
                } else {
                    environmentPrefix = this.resolveEnvironment(environment_prod, 'environment_prod', logging_config);
                }
            }

            this.storageService.addItemToLocalStorage(this.ENVIRONMENT_PREFIX_STORAGE_KEY, environmentPrefix, 'addEnvironmentPrefixToLocalStorage()').subscribe(() => {
                this.logger.info('setEnvironmentVariablesFromUrl() - environmentPrefix: ' + environmentPrefix);
                observer.next(environmentPrefix);
                observer.complete();
            });
        });
    }

    public get getEnvironmentEndpoint() {
        // No logging here: this getter is hit on essentially every endpoint access,
        // so logging would flood the console / log appenders.
        return this.currentEnvironmentEndpoints;
    }

    public resetEnvironmentEndpoints(): Observable<void> {
        this.logger.info('resetEnvironmentEndpoints()');
        return new Observable((observer) => {
            this.storageService.removeStoredItems([this.ENVIRONMENT_PREFIX_STORAGE_KEY]).subscribe(() => {
                // Resets to prod — or, while the test override is on, to the override target
                // (see environment.test-override.ts). LoginPage.ngOnInit() calls this on every
                // native load, so it's what points the IDP buttons at the override environment.
                this.environmentPrefix = this.resolveEnvironment(environment_prod, 'environment_prod', logging_config);
                observer.next();
                observer.complete();
            });
        });
    }

    // Applies the "default" environment and returns its storage prefix. While the test
    // override is enabled (environment.test-override.ts) it diverts to the override target
    // instead of the passed-in normal environment; with the override off it applies the
    // normal environment unchanged. Centralises every prod/default fallback so the whole
    // behaviour can be toggled from one place.
    private resolveEnvironment(normalEndpoints: any, normalPrefix: string, normalLoggingConfig: any): string {
        if (TEST_ENV_OVERRIDE) {
            this.currentEnvironmentEndpoints = TEST_ENV_OVERRIDE.endpoints;
            this.setLogginConfig(logging_config_dev);
            return TEST_ENV_OVERRIDE.prefix;
        }
        this.currentEnvironmentEndpoints = normalEndpoints;
        this.setLogginConfig(normalLoggingConfig);
        return normalPrefix;
    }

    private setLogginConfig(loggingConfig) {
        this.logger.info('setLogginConfig()');
        // this.loggingService.configure(loggingConfig)
        this.loggingService.configure(logging_config_dev);
    }

    // public getEnvironmentEndpointUrl(endpointName: string): Observable<string> {
    //     this.logger.info('getEnvironmentEndpoint()');
    //     // let baseEndpoint = environment[endpointName];
    //     // console.log('baseEndpoint', baseEndpoint);
    //     console.log('endpointName', endpointName);
    //     console.log('this.environmentPrefix', this.environmentPrefix);
    //     this[endpointName] = endpointName;
    //     switch (this.environmentPrefix) {
    //         case 'environment_us_prod': {
    //             return environment_us_prod[this[endpointName]];
    //         }
    //         case 'environment_us_test': {
    //             return environment_us_test[this[endpointName]];
    //         }
    //         case 'environment_us_dev': {
    //             return environment_us_dev[this[endpointName]];
    //         }
    //         case 'environment_test': {
    //             return environment_test[this[endpointName]];
    //         }
    //         case 'environment_dev': {
    //             return environment_dev[this[endpointName]];
    //         }
    //         default: {
    //             console.log('this[endpointName]', this[endpointName]);
    //             return environment_prod[this[endpointName]];
    //         }

    //     }
    // }

    // setEnvironmentVariablesFromUrl(url) {
     // ((\b(?:\.us|\.us\.devenv|\.us\.testenv)\b)?\.printix.net)
        // if (url.match("(.[a-z]{2})?(.us|.us.devenv|.us.testenv)?.printix.net/.*")) {
        //     switch (url) {
        //         case url.match("(.[a-z]{2})?.us.devenv.printix.net/.*"): {
        //             return 'environment_us_dev';
        //         }
        //         case url.match("(.[a-z]{2})?.us.testenv.printix.net/.*"): {
        //             return 'environment_us_test';
        //         }
        //         default: {
        //             return 'environment_us_prod';
        //         }
        //     }
        // } else if (url.match("(.[a-z]{2})?(.devenv|.testenv)?.printix.net/.*")) {
        //     switch (url) {
        //         case url.match("(.[a-z]{2})?.devenv?.printix.net/.*"): {
        //             return 'environment_dev';
        //         }
        //         case url.match("(.[a-z]{2})?.testenv?.printix.net/.*"): {
        //             return 'environment_test';
        //         }
        //         default: {
        //             return 'environment_prod';
        //         }
        //     }
        // }

    // public getArrayOfEndpoints(endpointNames: Array<string>): Observable<Array<string>> {
    //     // let endPointsArray = [];
    //     return new Observable((observer) => {
    //         // endpointNames.forEach(name => {

    //         // });
    //         const endpointsArray: Array<any> = endpointNames.map((name) => {this.currentEnvironmentEndpoints[name];});
    //         observer.next(endpointsArray);
    //         observer.complete();
    //     });
    // }
}